import React, { useEffect } from 'react';
import { FieldHistoryContext, OpenPanelOptions } from './FieldHistoryContext';
import { FieldHistoryPanel } from './FieldHistoryPanel';
import { usePanelStore } from './usePanelStore';

interface FieldHistoryProviderProps {
  proNumber: number | string;
  /** Reserved for a future dbtable filter on the history endpoint (see design doc §3). */
  table: string;
  /** Current value of an order field by camelCase key (see FieldHistoryContext). */
  getFieldValue?: (fieldKey: string) => string;
  children: React.ReactNode;
}

interface PanelData {
  colnames: string[];
  title: string;
  anchor: { x: number; y: number };
}

function panelKey(proNumber: number | string, colnames: string[]): string {
  return `${proNumber}:${colnames.join(',')}`;
}

export const FieldHistoryProvider: React.FC<FieldHistoryProviderProps> = ({
  proNumber,
  getFieldValue,
  children,
}) => {
  const { panels, openPanel: storeOpenPanel, closePanel } = usePanelStore<PanelData>();

  const openPanel = ({ colnames, title, anchor }: OpenPanelOptions) => {
    storeOpenPanel(panelKey(proNumber, colnames), { colnames, title, anchor });
  };

  // A left click that lands outside every panel dismisses the front-most one,
  // so the stack peels off one panel per click. Clicks on a fieldmenu surface
  // (another panel, or the context menu that opens a new one) are ignored, and
  // so are right clicks — otherwise right-clicking a label to open a second
  // panel would close the first.
  useEffect(() => {
    if (panels.length === 0) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return;
      const target = event.target;
      if (target instanceof Element && target.closest('[data-fieldmenu-surface]')) return;
      const top = panels.reduce((a, b) => (b.z > a.z ? b : a));
      closePanel(top.key);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [panels, closePanel]);

  return (
    <FieldHistoryContext.Provider value={{ openPanel, getFieldValue }}>
      {children}
      {panels.map((panel) => (
        <FieldHistoryPanel
          key={panel.key}
          proNumber={proNumber}
          colnames={panel.data.colnames}
          title={panel.data.title}
          anchor={panel.data.anchor}
          z={panel.z}
          onClose={() => closePanel(panel.key)}
        />
      ))}
    </FieldHistoryContext.Provider>
  );
};
